import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { RiUserLine, RiCheckLine, RiCloseLine, RiSearchLine, RiMailLine, RiCalendarLine } from 'react-icons/ri';
import { API_BASE } from '../config';

/* ─────────────────────────────────────────────────────────────
   COLOR PALETTE
───────────────────────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F7FF",
  softBlue: "#EBF1FF",
  primary: "#1A56DB",
  primaryDark: "#1240A8",
  primaryLight: "#3B82F6",
  navy: "#0F2557",
  navyDeep: "#091A3E",
  accent: "#60A5FA",
  accentGold: "#F59E0B",
  text: "#0F1F3D",
  textMid: "#374966",
  textMuted: "#6B7FA3",
  border: "#D6E4FF",
  borderLight: "#EBF1FF",
  shadow: "0 4px 24px rgba(26,86,219,0.08)",
  shadowMd: "0 8px 40px rgba(26,86,219,0.12)",
  shadowLg: "0 20px 60px rgba(26,86,219,0.16)",
  gradPrimary: "linear-gradient(135deg, #1A56DB 0%, #3B82F6 100%)",
  gradNavy: "linear-gradient(135deg, #0F2557 0%, #1A56DB 100%)",
  gradSoft: "linear-gradient(135deg, #EBF1FF 0%, #F4F7FF 100%)",
  gradGold: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)",
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const usersArray = Array.isArray(data) ? data : data.users || [];
        setUsers(usersArray);
      } else {
        setUsers(getDummyUsers());
      }
    } catch (error) {
      console.log('Failed to fetch users, using dummy data');
      setUsers(getDummyUsers());
    } finally {
      setLoading(false);
    }
  };

  const getDummyUsers = () => {
    return [
      { _id: '1', firstName: 'John', lastName: 'Doe', email: 'john@example.com', registrationStatus: 'confirmed', createdAt: '2024-01-15', country: 'USA' },
      { _id: '2', firstName: 'Jane', lastName: 'Smith', email: 'jane@example.com', registrationStatus: 'pending', createdAt: '2024-01-16', country: 'UK' },
      { _id: '3', firstName: 'Mike', lastName: 'Johnson', email: 'mike@example.com', registrationStatus: 'confirmed', createdAt: '2024-01-17', country: 'Canada' },
      { _id: '4', firstName: 'Sarah', lastName: 'Williams', email: 'sarah@example.com', registrationStatus: 'cancelled', createdAt: '2024-01-18', country: 'Australia' },
      { _id: '5', firstName: 'Alex', lastName: 'Brown', email: 'alex@example.com', registrationStatus: 'pending', createdAt: '2024-01-19', country: 'Germany' },
    ];
  };

  const filteredUsers = users.filter(user => {
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
    const matchesSearch = fullName.includes(searchTerm.toLowerCase()) ||
                         (user.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || user.registrationStatus === filter;
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    const colors = {
      confirmed: { bg: T.softBlue, text: T.primary, border: T.primary },
      pending: { bg: '#FEF3C7', text: T.accentGold, border: T.accentGold },
      cancelled: { bg: '#FEE2E2', text: '#EF4444', border: '#EF4444' }
    };
    return colors[status] || { bg: T.borderLight, text: T.textMuted, border: T.textMuted };
  };

  const getStatusLabel = (status) => {
    const labels = {
      confirmed: 'Confirmed',
      pending: 'Pending',
      cancelled: 'Cancelled'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '256px'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: `3px solid ${T.accentGold}20`,
          borderTopColor: T.accentGold,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{
        fontSize: '30px',
        fontWeight: 'bold',
        color: T.navy,
        marginBottom: '32px'
      }}>
        User Management
      </h1>

      {/* Search and Filter Section */}
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
          <RiSearchLine style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: T.textMuted
          }} />
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 12px 12px 40px',
              background: T.white,
              border: `1px solid ${T.border}`,
              borderRadius: '8px',
              color: T.text,
              fontSize: '14px',
              transition: 'all 0.2s',
              outline: 'none',
            }}
            onFocus={(e) => e.currentTarget.style.borderColor = T.primary}
            onBlur={(e) => e.currentTarget.style.borderColor = T.border}
          />
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {['all', 'confirmed', 'pending', 'cancelled'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                textTransform: 'capitalize',
                transition: 'all 0.2s',
                cursor: 'pointer',
                background: filter === status ? T.primary : T.white,
                color: filter === status ? T.white : T.textMid,
                border: filter === status ? 'none' : `1px solid ${T.border}`,
              }}
              onMouseEnter={(e) => {
                if (filter !== status) {
                  e.currentTarget.style.background = T.softBlue;
                }
              }}
              onMouseLeave={(e) => {
                if (filter !== status) {
                  e.currentTarget.style.background = T.white;
                }
              }}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Users Table */}
      <div style={{
        background: T.white,
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: T.shadowMd,
        border: `1px solid ${T.borderLight}`,
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                <th style={{ textAlign: 'left', padding: '16px', color: T.textMuted, fontWeight: 600, fontSize: '14px' }}>User</th>
                <th style={{ textAlign: 'left', padding: '16px', color: T.textMuted, fontWeight: 600, fontSize: '14px' }}>Email</th>
                <th style={{ textAlign: 'left', padding: '16px', color: T.textMuted, fontWeight: 600, fontSize: '14px' }}>Status</th>
                <th style={{ textAlign: 'left', padding: '16px', color: T.textMuted, fontWeight: 600, fontSize: '14px' }}>Date</th>
                <th style={{ textAlign: 'left', padding: '16px', color: T.textMuted, fontWeight: 600, fontSize: '14px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ padding: '32px', textAlign: 'center', color: T.textMuted }}>
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user, index) => {
                  const statusColors = getStatusColor(user.registrationStatus);
                  return (
                    <motion.tr
                      key={user._id || index}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                      style={{
                        borderBottom: `1px solid ${T.borderLight}`,
                        transition: 'background 0.2s ease',
                      }}
                      whileHover={{ backgroundColor: T.offWhite }}
                    >
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: T.gradPrimary,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                            <span style={{ color: T.white, fontWeight: 600, fontSize: '14px' }}>
                              {(user.firstName || 'U').charAt(0)}
                            </span>
                          </div>
                          <div>
                            <div style={{ color: T.text, fontWeight: 500 }}>
                              {user.firstName} {user.lastName}
                            </div>
                            {user.country && (
                              <div style={{ color: T.textMuted, fontSize: '12px' }}>
                                {user.country}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <RiMailLine style={{ color: T.textMuted, fontSize: '14px' }} />
                          <span style={{ color: T.textMid, fontSize: '14px' }}>{user.email}</span>
                        </div>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '9999px',
                          fontSize: '12px',
                          fontWeight: 500,
                          background: statusColors.bg,
                          color: statusColors.text,
                        }}>
                          {getStatusLabel(user.registrationStatus)}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <RiCalendarLine style={{ color: T.textMuted, fontSize: '14px' }} />
                          <span style={{ color: T.textMid, fontSize: '14px' }}>
                            {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            style={{
                              padding: '8px',
                              background: '#E8F5E9',
                              color: '#4CAF50',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'background 0.2s ease',
                            }}
                            whileHover={{ backgroundColor: '#C8E6C9' }}
                            title="Confirm User"
                          >
                            <RiCheckLine size={18} />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            style={{
                              padding: '8px',
                              background: '#FFEBEE',
                              color: '#F44336',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'background 0.2s ease',
                            }}
                            whileHover={{ backgroundColor: '#FFCDD2' }}
                            title="Cancel User"
                          >
                            <RiCloseLine size={18} />
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stats Summary */}
      <div style={{
        marginTop: '24px',
        padding: '16px 24px',
        background: T.gradSoft,
        borderRadius: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <RiUserLine style={{ color: T.primary }} />
          <span style={{ color: T.textMid, fontSize: '14px' }}>
            Total Users: <strong style={{ color: T.navy }}>{users.length}</strong>
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '8px', height: '8px', backgroundColor: T.primary, borderRadius: '50%' }} />
          <span style={{ color: T.textMid, fontSize: '14px' }}>
            Confirmed: <strong style={{ color: T.navy }}>{users.filter(u => u.registrationStatus === 'confirmed').length}</strong>
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '8px', height: '8px', backgroundColor: T.accentGold, borderRadius: '50%' }} />
          <span style={{ color: T.textMid, fontSize: '14px' }}>
            Pending: <strong style={{ color: T.navy }}>{users.filter(u => u.registrationStatus === 'pending').length}</strong>
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '8px', height: '8px', backgroundColor: '#EF4444', borderRadius: '50%' }} />
          <span style={{ color: T.textMid, fontSize: '14px' }}>
            Cancelled: <strong style={{ color: T.navy }}>{users.filter(u => u.registrationStatus === 'cancelled').length}</strong>
          </span>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;